import { Group, Stack, Textarea, Typography } from "@mantine/core";
import { useDebouncedCallback } from "@mantine/hooks";
import {
    addComposerChild$,
    BlockTypeSelect,
    BoldItalicUnderlineToggles,
    CreateLink,
    headingsPlugin,
    InsertThematicBreak,
    linkDialogPlugin,
    linkDialogState$,
    linkPlugin,
    listsPlugin,
    ListsToggle,
    MDXEditor,
    realmPlugin,
    thematicBreakPlugin,
    toolbarPlugin,
    UndoRedo,
    updateLink$,
    useCellValue,
    usePublisher,
} from "@mdxeditor/editor";
import { useEffect } from "react";
import { useComposeFormContext } from "shared/components/message/compose/context";
import { useColorMode } from "shared/hooks/useColorMode";
import { useFormState } from "shared/hooks/useFormState";
import { mdToTxt } from "shared/utils/md";

// https://github.com/mdx-editor/editor/discussions/614#discussion-7361996
const ensureExternalLinkPlugin = realmPlugin({
    init(realm) {
        realm.pubIn({
            [addComposerChild$]: () => {
                const link = useCellValue(linkDialogState$);
                const updateLink = usePublisher(updateLink$);

                useEffect(() => {
                    if (link.type === "preview" && link.url !== "" && !link.url.startsWith("http")) {
                        updateLink({
                            title: link.title,
                            url: `https://${link.url}`,
                        });
                    }
                }, [link]);

                return null;
            },
        });
    },
});

export const MessageBodyEditor = () => {
    const form = useComposeFormContext();
    const [richbody, setRichbody] = useFormState(form, "richBody");
    const [, setBody] = useFormState(form, "body");
    const [mode] = useFormState(form, "meta.mode");

    const handleRichBodyChange = useDebouncedCallback(
        (richbody: string) => {
            void mdToTxt(richbody).then(setBody);
        },
        { delay: 100 },
    );

    useEffect(() => {
        handleRichBodyChange(richbody);
    }, [richbody]);

    const { isDark } = useColorMode();

    if (mode == "read") {
        return <Textarea autosize aria-label="Message body" readOnly value={richbody} />;
    }

    return (
        <Stack>
            <Typography aria-disabled>
                <MDXEditor
                    placeholder="Let people know what's going on..."
                    className={isDark ? "dark-theme" : ""}
                    plugins={[
                        toolbarPlugin({
                            toolbarContents: () => (
                                <Group>
                                    <UndoRedo />
                                    <BoldItalicUnderlineToggles options={["Bold", "Italic"]} />
                                    <BlockTypeSelect />
                                    <CreateLink />
                                    <InsertThematicBreak />
                                    <ListsToggle />
                                </Group>
                            ),
                        }),
                        ensureExternalLinkPlugin(),
                        headingsPlugin({ allowedHeadingLevels: [1, 2, 3, 4, 5, 6] }),
                        linkPlugin(),
                        linkDialogPlugin(),
                        thematicBreakPlugin(),
                        listsPlugin(),
                    ]}
                    markdown={richbody}
                    onChange={(md) => {
                        setRichbody(
                            md
                                .split("\n")
                                // https://github.com/awslabs/threat-composer/commit/897385f866d51659db7d8fb300dc722fb25388dd#diff-c5a7174968412ec82cb6e568d5f387154608c1248a5d446684a9beb3df832104R27-R50"
                                // https://github.com/mdx-editor/editor/issues/103
                                .map((line) => (line.endsWith("&#x20;") ? line.slice(0, -6) : line))
                                .join("\n"),
                        );
                    }}
                />
            </Typography>
        </Stack>
    );
};
